import React, {Suspense, useEffect, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryDateCalendarProps} from '@di-shared/features/history/date/HistoryDateCalendar/index';
import useTripDayEu, {getWeekIndexOfDate, isTripOnDay} from '@di-shared/features/history/date/useTripDay.eu';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {isSameYearAndMonth, setDay, startDateOfDay, startDateOfMonth} from '@shared/dateUtils';


export default function ({vin, selectedDate, setSelectedDate, isMonthMode, isMonthChange, setIsMonthChange}: HistoryDateCalendarProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          isMonthMode={isMonthMode}
          isMonthChange={isMonthChange}
          setIsMonthChange={setIsMonthChange}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, setSelectedDate, isMonthMode, isMonthChange, setIsMonthChange}: HistoryDateCalendarProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {focusTripDate, minDate, tripDays} = useTripDayEu({vin, selectedDate});
  const [weekIndex, setWeekIndex] = useState(getWeekIndexOfDate(selectedDate));

  useEffect(() => {
    setSelectedDate(focusTripDate);
    setWeekIndex(getWeekIndexOfDate(focusTripDate));
    setIsMonthChange(false);
  }, [isMonthChange]);

  // 월 변경 로딩화면
  if (isMonthChange) {
    return <FallbackLoading />;
  }

  return (
    <ReactDatePicker
      inline
      locale={getDateFnsLocale()}
      selected={selectedDate}
      minDate={isSameYearAndMonth(minDate, selectedDate) ? startDateOfDay(setDay(selectedDate, tripDays[0].day)) : minDate}
      maxDate={today}
      filterDate={(date) => isSameYearAndMonth(date, selectedDate)}
      disabledKeyboardNavigation
      calendarClassName={clsx(!isMonthMode && `show-first-week-${weekIndex + 1}`)}
      useWeekdaysShort
      onChange={(date: Date) => {
        setWeekIndex(getWeekIndexOfDate(date));
        setSelectedDate(date);
      }}
      onMonthChange={(date) => {
        setIsMonthChange(true);
        setSelectedDate(date);
      }}
      renderCustomHeader={({date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled}) => (
        <div className="react-datepicker__current-month">
          <button
            type="button"
            aria-label="Previous Month"
            className={clsx('react-datepicker__navigation react-datepicker__navigation--previous', {
              'navigation--disabled': prevMonthButtonDisabled,
            })}
            disabled={prevMonthButtonDisabled}
            onClick={() => decreaseMonth()}
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
          </button>
          {CldrDatetimeFormatter.format(date, 'yMMM')}
          <button
            type="button"
            aria-label="Next Month"
            className={clsx('react-datepicker__navigation react-datepicker__navigation--next', {
              'navigation--disabled': nextMonthButtonDisabled,
            })}
            disabled={nextMonthButtonDisabled}
            onClick={() => increaseMonth()}
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
          </button>
        </div>
      )}
      renderDayContents={(dayOfMonth, date: Date) => (
        <>
          {dayOfMonth}
          {isSameYearAndMonth(date, selectedDate) && isTripOnDay(tripDays, dayOfMonth) && (
            <span className="has_data">
              <span className="blind">{t('ctn_home_information')}</span>
            </span>
          )}
        </>
      )}
    />
  );
}

function FallbackLoading() {
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="react-datepicker">
      <div className="react-datepicker__month-container skeleton">
        <div className="react-datepicker__header">
          <div className="react-datepicker__current-month skeleton">
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              aria-label="Previous Month"
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">Previous Month</span>
            </button>
            {CldrDatetimeFormatter.format(today, 'yMMM')}
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--next navigation--disabled"
              aria-label="Next Month"
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">Next Month</span>
            </button>
          </div>
          <div
            className="react-datepicker__day-names"
            style={{height: '260px'}}
          />
        </div>
      </div>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const startDate = isSameYearAndMonth(selectedDate, today) ? today : startDateOfMonth(selectedDate);

  return (
    <ReactDatePicker
      inline
      locale={getDateFnsLocale()}
      disabledKeyboardNavigation
      selected={startDate}
      onChange={() => {}}
      useWeekdaysShort
      renderCustomHeader={({date}) => (
        <div className="react-datepicker__current-month">
          <button
            type="button"
            aria-label="Previous Month"
            className="react-datepicker__navigation react-datepicker__navigation--previous"
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
          </button>
          {CldrDatetimeFormatter.format(date, 'yMMM')}
          <button
            type="button"
            aria-label="Next Month"
            className="react-datepicker__navigation react-datepicker__navigation--next"
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
          </button>
        </div>
      )}
    />
  );
}
